import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

const UeberUnsPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Über uns - AfD Bad Windsheim Stadtratskandidaten',
    description: 'Lernen Sie die Stadtrats-Kandidaten der AfD Bad Windsheim kennen. Wir stehen für Transparenz, Offenheit, Bürgernähe und den Erhalt unserer Heimat und christlich geprägten Lebensweisen.',
    keywords: 'AfD Bad Windsheim, Über uns, Stadtratskandidaten, Heimat, Integration, Demokratie, Transparenz',
    canonical: `${siteUrl}/ueber-uns`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Über uns - AfD Bad Windsheim',
      description: 'Informationen über die Stadtrats-Kandidaten der AfD Bad Windsheim',
      url: `${siteUrl}/ueber-uns`,
      mainEntity: {
        '@type': 'Organization',
        name: 'AfD Bad Windsheim',
        description: 'Stadtrats-Kandidaten der AfD Bad Windsheim für Transparenz, Offenheit und Bürgernähe'
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-primary-600 to-primary-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-sm font-semibold tracking-wider uppercase">Über uns</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Herzlich willkommen
          </h1>
          <p className="text-xl text-primary-100 max-w-3xl">
            bei den Stadtrats-Kandidaten der AfD Bad Windsheim
          </p>
        </div>
      </section>

      {/* Logo Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary-200 to-secondary-200 rounded-2xl opacity-20"></div>
            <img 
              src="https://static.readdy.ai/image/2461e108107748ba3465ce747c79b9cd/ad92e76f525a3092b6db20d0bc38e3ae.png"
              alt="AfD Bad Windsheim Logo"
              className="relative w-full max-w-2xl mx-auto rounded-2xl shadow-2xl border-4 border-white"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          
          {/* Unsere Heimat */}
          <div className="mb-16">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Unsere Werte</span>
            </div>
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Unsere Heimat und Kultur</h2>
            
            <div className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-xl border-l-4 border-primary-600 mb-8">
              <p className="text-gray-700 leading-relaxed text-lg mb-4">
                Wir lieben unsere Heimat und möchten, dass unsere Kultur und unsere christlich geprägten Lebensweisen erhalten bleiben aber auch von denen geachtet werden, die sich unser Land - aus ihren subjektiv verständlichen Gründen - als ihre neue Heimat ausgesucht haben.
              </p>
              <p className="text-gray-700 leading-relaxed text-lg">
                Wir haben hier in Bad Windsheim viele positive Beispiele für eine gelungene Integration.
              </p>
            </div>
          </div>

          {/* Demokratie und Meinungsfreiheit */}
          <div className="mb-16">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Demokratie</span>
            </div>
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Für echte Demokratie und Meinungsfreiheit</h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-secondary-50 to-white p-8 rounded-xl border-l-4 border-secondary-600">
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  Leider wollen viele unserer Gegner nicht mit uns auf Augenhöhe diskutieren, sondern lieber mit ihren Vorurteilen (Narrativen), einseitigen Parolen und unberechtigten Vorurteilen dafür sorgen, dass die demokratische Meinungsbildung nur in deren ideologisch verblendeten Denkweisen ausgeübt werden darf.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Das ist nicht nur schade, sondern es zerstört auch die Demokratie und die individuelle Freiheit aller Menschen.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-xl border-l-4 border-primary-600">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Unsere Alternative</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Und genau diese Brandmauern wollen wir als eine echte Alternative für Deutschland in Bad Windsheim und in unserem Landkreis mit konstruktiver Arbeit überflüssig machen.
                </p>
              </div>
            </div>
          </div>

          {/* Unser Versprechen */}
          <div className="mb-16">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Unser Versprechen</span>
            </div>
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Transparenz, Offenheit und Bürgernähe</h2>
            
            <div className="bg-gradient-to-br from-primary-600 to-primary-700 text-white p-10 rounded-xl shadow-xl">
              <p className="text-xl leading-relaxed mb-6">
                Wir werden im Stadtrat für Transparenz, Offenheit und Bürgernähe kämpfen.
              </p>
              <p className="text-lg text-primary-100 leading-relaxed">
                Auf den folgenden Seiten finden Sie auch unsere Haltung zur Migration, zu bezahlbarem Wohnen, zu Bad Windsheim als Stadt der Gesundheit und zum Umweltschutz.
              </p>
            </div>
          </div>

          {/* Themen-Links */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 text-center">Erfahren Sie mehr über unsere Positionen</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Link 
                to="/programm"
                className="group bg-gradient-to-br from-secondary-50 to-white p-6 rounded-xl border-2 border-secondary-200 hover:border-secondary-600 transition-all duration-300 hover:shadow-lg cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-secondary-600 text-white rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-1">Unser Programm</h4>
                    <p className="text-sm text-gray-600">Alle Themen im Detail</p>
                  </div>
                </div>
              </Link>

              <Link 
                to="/kandidaten"
                className="group bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl border-2 border-primary-200 hover:border-primary-600 transition-all duration-300 hover:shadow-lg cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-team-line text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-1">Unsere Kandidaten</h4>
                    <p className="text-sm text-gray-600">Lernen Sie uns kennen</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-secondary-600 to-secondary-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Deshalb bitten wir Sie um Ihre Stimme
          </h2>
          <p className="text-xl mb-8 text-red-100">
            Gemeinsam für eine bessere Zukunft in Bad Windsheim
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link 
              to="/stadtratswahl" 
              className="bg-white text-secondary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
            >
              Zur Stadtratswahl
            </Link>
            <Link 
              to="/kreistagswahl" 
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Zur Kreistagswahl
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UeberUnsPage;
